import { useEffect, useRef, type ReactNode } from 'react';
import { AuthProvider as OidcProvider, useAuth, type AuthContextProps } from 'react-oidc-context';
import { queryClient } from '../app/queryClient';
import { configureApiClient } from '../services/api';
import { oidcConfig, olvidarReautenticacion } from './oidc';

/**
 * Conecta la sesion OIDC con el cliente HTTP.
 *
 * Reglas de manejo de sesion:
 *  - El token vive en memoria de `oidc-client-ts` (respaldado en
 *    sessionStorage), nunca se copia a otro sitio.
 *  - Ante un 401 se intenta UNA renovacion silenciosa; si falla, se vuelve a
 *    Keycloak. El contador se reinicia con cada login correcto, de modo que
 *    no se puede formar un bucle de redirecciones.
 *
 * ## Por que el registro NO va en un efecto
 *
 * Fallo real, y de los que no se reproducen siempre: al recargar la pagina,
 * las peticiones salian SIN cabecera `Authorization` y el servidor respondia
 * 401 `MISSING_TOKEN`. La sesion estaba perfectamente: el token existia.
 *
 * La causa es el orden en que React ejecuta los efectos. Los de los HIJOS
 * corren ANTES que los del padre, y este puente es el padre de toda la
 * aplicacion. Asi que las consultas de las pantallas se disparaban mientras
 * `getAccessToken` seguia siendo el valor inicial —el que devuelve `null`— y
 * tambien mientras `onUnauthorized` seguia sin hacer nada. Resultado: peticion
 * sin token, 401, y nadie que renovara. Como ademas un 401 no se reintenta a
 * proposito, la pantalla se quedaba con «no se pudo cargar» y el menu con
 * «Cargando…» para siempre. Moverse a otra pagina lo arreglaba porque montaba
 * consultas nuevas, ya con el proveedor puesto.
 *
 * La solucion es registrar el proveedor DURANTE EL RENDER, antes de que ningun
 * hijo llegue a montarse, y hacer que lea siempre del `auth` vivo a traves de
 * una referencia. Asi nunca hay una ventana sin token ni una closure vieja.
 */
function ApiBridge({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();

  /*
   * El `auth` vivo, accesible desde closures que se crean una sola vez.
   *
   * Se asigna en el cuerpo del componente —durante el render— y no en un
   * efecto: es justo el momento que hay que ganarle a los hijos.
   */
  const authRef = useRef<AuthContextProps>(auth);
  authRef.current = auth;

  const renewAttempted = useRef(false);
  const registrado = useRef(false);

  if (!registrado.current) {
    registrado.current = true;

    configureApiClient({
      tokenProvider: () => authRef.current.user?.access_token ?? null,
      onUnauthorized: () => {
        if (renewAttempted.current) return;
        renewAttempted.current = true;

        const sesion = authRef.current;
        void sesion
          .signinSilent()
          .then((user) => {
            if (!user) {
              void sesion.signinRedirect();
            }
          })
          .catch(() => {
            void sesion.signinRedirect();
          });
      },
    });
  }

  useEffect(() => {
    if (auth.isAuthenticated) {
      renewAttempted.current = false;
      // Entro bien: la proxima caducidad vuelve a tener derecho a su intento
      // silencioso, sin que cuente el de hace un momento.
      olvidarReautenticacion();
    }
  }, [auth.isAuthenticated]);

  /*
   * Renovar al volver a la pestana.
   *
   * `automaticSilentRenew` programa la renovacion con un temporizador, y un
   * temporizador no es fiable cuando nadie mira: los navegadores los frenan en
   * pestanas de fondo y el equipo los congela al suspenderse. Quien deja ED
   * Ecosystem abierto y vuelve al rato se encuentra con que el aviso de «voy a
   * renovar» salto tarde o no salto, el refresh token ya no vale, y Keycloak
   * responde «Session not active». Esa era la interrupcion que se veia.
   *
   * Aqui se comprueba al volver a mirar, que es justo el momento en que el
   * temporizador pudo haberse perdido. Si al token le queda poco —o ya
   * caduco—, se renueva antes de que nadie pulse nada.
   *
   * Si la renovacion falla, no se hace ruido: el error llega a `auth.error` y
   * de ahi lo recoge `RequireAuth`, que sabe distinguir una sesion caducada de
   * un Keycloak caido. Duplicar aqui esa decision seria tener dos sitios donde
   * se decide lo mismo.
   */
  useEffect(() => {
    const MARGEN_SEGUNDOS = 90;

    const alVolverAMirar = (): void => {
      if (document.visibilityState !== 'visible') return;

      const sesion = authRef.current;
      if (!sesion.isAuthenticated || sesion.activeNavigator || sesion.isLoading) return;

      const usuario = sesion.user;
      if (!usuario) return;

      const quedan = usuario.expires_in ?? 0;
      if (!usuario.expired && quedan > MARGEN_SEGUNDOS) return;

      void sesion.signinSilent().catch(() => {
        // A proposito en silencio: lo resuelve el guard.
      });
    };

    document.addEventListener('visibilitychange', alVolverAMirar);
    return () => document.removeEventListener('visibilitychange', alVolverAMirar);
  }, []);

  /*
   * Cuando llega un token NUEVO, se vuelve a pedir lo que fallo sin el.
   *
   * Sin esto, la renovacion silenciosa funciona pero no sirve de nada: las
   * consultas que respondieron 401 se quedan en estado de error y, como los
   * 401 no se reintentan, ahi se quedan hasta que alguien recarga o cambia de
   * pantalla. Justo el sintoma que se veia.
   *
   * Solo se invalida en los CAMBIOS de token, nunca con el primero: al
   * arrancar, las consultas todavia no han pedido nada y volver a lanzarlas
   * seria pedir todo dos veces.
   */
  const tokenAnterior = useRef<string | null>(null);
  const token = auth.user?.access_token ?? null;

  useEffect(() => {
    if (!token) return;

    const habiaOtro = tokenAnterior.current !== null && tokenAnterior.current !== token;
    tokenAnterior.current = token;

    if (habiaOtro) {
      void queryClient.invalidateQueries();
    }
  }, [token]);

  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  return (
    <OidcProvider {...oidcConfig}>
      <ApiBridge>{children}</ApiBridge>
    </OidcProvider>
  );
}
